import React, { useRef, useState, useLayoutEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

const SvgStyled = styled.svg`
  display: block;
  margin: 0;
  width: 2rem;
  height: auto;
  animation-name: ${keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
  `};
  animation-duration: 540ms;
  animation-delay: 1080ms;
  animation-fill-mode: backwards;
  animation-timing-function: ease-in-out;

  ${({ centered }: { centered?: boolean }) =>
    centered &&
    css`
      margin-left: auto;
      margin-right: auto;
    `}
`;

const PathAnimation = styled.path`
  fill-opacity: 0;
  stroke: ${({ theme }) => theme.palette.indicator.negative};
  stroke-width: 3;
  animation-duration: 540ms;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  ${({ pathLength }: { pathLength?: number }) => {
    if (!pathLength) {
      return;
    }

    const dash = pathLength / 4;
    const gap = pathLength - dash;

    return css`
      stroke-dasharray: ${dash}, ${gap};
      animation-name: ${keyframes`
        0% {
          stroke-dashoffset: ${pathLength};
        }
        100% {
          stroke-dashoffset: 0;
        }
      `};
    `;
  }}
`;

export const LoadingIndicator: React.FC<PropTypes<typeof SvgStyled>> = ({
  ref,
  ...restProps
}) => {
  const [pathLength, setPathLength] = useState(0);
  const pathRef = useRef<SVGPathElement>(null);

  useLayoutEffect(() => {
    if (!pathRef || !pathRef.current) {
      return;
    }
    // Check the existence of getTotalLength function because jsdom doesn't provide one
    const pathLength =
      (pathRef.current.getTotalLength && pathRef.current.getTotalLength()) || 0;
    setPathLength(pathLength);
  }, []);

  return (
    <SvgStyled
      width="32"
      height="32"
      {...restProps}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any} // Styled components' & React.SVGProps<SVGSVGElement>'s ref types are incompatible
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
    >
      <PathAnimation
        ref={pathRef}
        pathLength={pathLength}
        d="M29.2,6.3c-0.7,9.1-5.5,17.4-13.2,22.3C8.3,23.6,3.5,15.3,2.8,6.2C6.9,4.1,11.4,3,15.9,3C20.5,3,25.1,4.2,29.2,6.3L29.2,6.3z"
      />
    </SvgStyled>
  );
};

export const AbsolutelyCenteredLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
